/*
 * SHLC cart behaviour.
 *
 * Everything the page needs to decide is worked out in Liquid and handed over
 * on data attributes, so this file only performs actions. It never re-renders
 * the cart itself. After any change it reloads, which keeps the free-gift
 * state, the bundle swap-up and the totals consistent with one another.
 */
(function () {
  'use strict';

  var GIFT_GUARD = 'shlcCartGiftGuard';
  var CODE_GUARD = 'shlcCartGiftCodeGuard';
  var CODE_FAIL = 'shlcCartGiftCodeFail';
  var MMK_GUARD = 'shlcCartMmkGuard';
  var MMK_NOTE = 'shlcCartMmkNote';

  /* Any cart change can move the gift in or out of qualifying, so every
     mutation clears all three gift guards and lets the next render decide. */
  function resetGiftGuards() {
    sessionStorage.removeItem(GIFT_GUARD);
    sessionStorage.removeItem(CODE_GUARD);
    sessionStorage.removeItem(CODE_FAIL);
  }

  function money(cents) {
    return (cents / 100).toFixed(2);
  }

  function post(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (r) {
      if (!r.ok) throw new Error(url + ' returned ' + r.status);
      return r.json();
    });
  }

  function busy(btn) {
    if (!btn) return;
    btn.classList.add('is-busy');
    btn.setAttribute('aria-busy', 'true');
  }

  function unbusy(btn) {
    if (!btn) return;
    btn.classList.remove('is-busy');
    btn.removeAttribute('aria-busy');
  }

  function showError(msg) {
    var box = document.querySelector('[data-shlc-cart-error]');
    if (!box) return;
    box.textContent = msg;
    box.hidden = false;
  }

  /* ------------------------------------------------------------------
   * Free gift, part one: the discount code.
   *
   * The gift is 100% off a named product, delivered by a discount code
   * rather than an automatic discount, because an automatic Buy X Get Y
   * would count the lesson lines as its prerequisite and Shopify then
   * refuses to also apply the mix and match lesson discount to them.
   * The code carries no minimum of its own, so the spend gate lives in
   * Liquid and this function only applies what Liquid asked for.
   *
   * Returns true when it has started a reload, so the caller knows not
   * to also run the add or remove pass in the same page view.
   * ---------------------------------------------------------------- */
  function syncGiftCode(root) {
    var needs = root.getAttribute('data-gift-needs-code') === '1';
    var code = (root.getAttribute('data-gift-code') || '').trim();

    if (!needs || !code) {
      sessionStorage.removeItem(CODE_GUARD);
      sessionStorage.removeItem(CODE_FAIL);
      return false;
    }

    if (sessionStorage.getItem(CODE_GUARD) !== code) {
      sessionStorage.setItem(CODE_GUARD, code);
      fetch(window.Shopify.routes.root + 'discount/' + encodeURIComponent(code), {
        credentials: 'same-origin',
      })
        .then(function () {
          window.location.reload();
        })
        .catch(function () {
          /* Guard stays set, so the fallback below runs on the next view. */
        });
      return true;
    }

    /* The code has already been applied and the gift is still being
       charged for, so something is wrong with the code itself. Take the
       gift back out rather than charge for what the page called free. */
    var lineKey = root.getAttribute('data-gift-key');
    if (lineKey && sessionStorage.getItem(CODE_FAIL) !== lineKey) {
      sessionStorage.setItem(CODE_FAIL, lineKey);
      post(window.Shopify.routes.root + 'cart/change.js', { id: lineKey, quantity: 0 })
        .then(function () {
          window.location.reload();
        })
        .catch(function () {});
      return true;
    }

    return false;
  }

  /* ------------------------------------------------------------------
   * Free gift, part two. Liquid tells us whether the gift should be
   * added or removed. A session guard stops a failed add from looping.
   * ---------------------------------------------------------------- */
  function syncGift(root) {
    var action = root.getAttribute('data-gift-action');
    if (!action || action === 'none') {
      sessionStorage.removeItem(GIFT_GUARD);
      return;
    }

    var variantId = root.getAttribute('data-gift-variant');
    var lineKey = root.getAttribute('data-gift-key');
    if (action === 'add' && !variantId) return;
    if (action === 'remove' && !lineKey) return;

    var guard = action + ':' + (variantId || lineKey);
    if (sessionStorage.getItem(GIFT_GUARD) === guard) return;
    sessionStorage.setItem(GIFT_GUARD, guard);

    var req =
      action === 'add'
        ? post(window.Shopify.routes.root + 'cart/add.js', {
            items: [{ id: Number(variantId), quantity: 1 }],
          })
        : post(window.Shopify.routes.root + 'cart/change.js', { id: lineKey, quantity: 0 });

    req
      .then(function () {
        window.location.reload();
      })
      .catch(function () {
        /* Leave the guard in place so we do not thrash the cart. */
      });
  }

  /* ------------------------------------------------------------------
   * Add a single product to the cart, then reload.
   * ---------------------------------------------------------------- */
  function wireAddButtons() {
    document.querySelectorAll('[data-shlc-add]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var id = Number(btn.getAttribute('data-shlc-add'));
        if (!id) return;
        busy(btn);
        post(window.Shopify.routes.root + 'cart/add.js', { items: [{ id: id, quantity: 1 }] })
          .then(function () {
            window.location.reload();
          })
          .catch(function () {
            unbusy(btn);
            showError('That lesson could not be added. Please try again.');
          });
      });
    });
  }

  /* ------------------------------------------------------------------
   * Bundle swap. Drop the member lines already in the cart, add the
   * bundle, reload. Done as one /cart/update.js call so the cart is
   * never left half-changed.
   * ---------------------------------------------------------------- */
  function wireSwap() {
    var btn = document.querySelector('[data-shlc-swap]');
    if (!btn) return;

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var variantId = btn.getAttribute('data-shlc-swap');
      var keys = (btn.getAttribute('data-shlc-swap-keys') || '').split('|').filter(Boolean);
      if (!variantId) return;

      busy(btn);

      var updates = {};
      keys.forEach(function (k) {
        updates[k] = 0;
      });

      post(window.Shopify.routes.root + 'cart/update.js', { updates: updates })
        .then(function () {
          return post(window.Shopify.routes.root + 'cart/add.js', {
            items: [{ id: Number(variantId), quantity: 1 }],
          });
        })
        .then(function () {
          window.location.reload();
        })
        .catch(function () {
          unbusy(btn);
          showError('The bundle could not be swapped in. Please try again.');
        });
    });
  }

  /* ------------------------------------------------------------------
   * Marking pack consolidation.
   *
   * The multi-paper discount is baked into the variant prices, so it
   * only exists if the cart holds the right pack. Liquid works out, per
   * level, which lines to drop and which packs replace them; the two
   * functions below apply that either on load (mmkApply) or from the
   * papers stepper and the fallback button (wireMmkSwap).
   * ---------------------------------------------------------------- */

  function mmkParse(spec) {
    return (spec || '')
      .split('|')
      .filter(Boolean)
      .map(function (pair) {
        var bits = pair.split(':');
        return { id: Number(bits[0]), quantity: Number(bits[1]) };
      })
      .filter(function (i) {
        return i.id > 0 && i.quantity > 0;
      });
  }

  function mmkRepack(updates, items) {
    return post(window.Shopify.routes.root + 'cart/update.js', { updates: updates }).then(
      function () {
        return post(window.Shopify.routes.root + 'cart/add.js', { items: items });
      }
    );
  }

  /* ------------------------------------------------------------------
   * Apply every outstanding repack without being asked, then reload.
   *
   * The guard is keyed on the level and its paper count rather than on
   * the line keys, because re-adding a line mints a new key. Keying it
   * on the outcome means splitting a pack back apart survives a reload
   * instead of being instantly undone.
   * ---------------------------------------------------------------- */
  function mmkApply() {
    var panels = document.querySelectorAll('[data-shlc-mmk-auto]');
    if (!panels.length) {
      mmkRenderNote();
      return;
    }

    var updates = {};
    var items = [];
    var levels = [];
    var saving = 0;

    panels.forEach(function (p) {
      (p.getAttribute('data-shlc-mmk-keys') || '')
        .split('|')
        .filter(Boolean)
        .forEach(function (k) {
          updates[k] = 0;
        });
      items = items.concat(mmkParse(p.getAttribute('data-shlc-mmk-auto')));
      saving += Number(p.getAttribute('data-shlc-mmk-saving')) || 0;
      levels.push({
        level: p.getAttribute('data-shlc-mmk-level'),
        papers: Number(p.getAttribute('data-shlc-mmk-papers')) || 0,
      });
    });

    if (!items.length || !Object.keys(updates).length) {
      mmkRenderNote();
      return;
    }

    var guard = levels
      .map(function (l) {
        return l.level + ':' + l.papers;
      })
      .concat(
        items.map(function (i) {
          return i.id + 'x' + i.quantity;
        })
      )
      .join(',');

    if (sessionStorage.getItem(MMK_GUARD) === guard) {
      mmkRenderNote();
      return;
    }
    sessionStorage.setItem(MMK_GUARD, guard);

    /* Read the lines first so the change can be undone exactly. */
    fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var restore = cart.items
          .filter(function (i) {
            return Object.prototype.hasOwnProperty.call(updates, i.key);
          })
          .map(function (i) {
            return { id: i.variant_id, quantity: i.quantity };
          });

        return mmkRepack(updates, items).then(function () {
          sessionStorage.setItem(
            MMK_NOTE,
            JSON.stringify({ saving: saving, levels: levels, restore: restore, added: items })
          );
          resetGiftGuards();
          window.location.reload();
        });
      })
      .catch(function () {
        /* Leave the guard set so a failing cart is not hammered. The
           panel is still on screen with its button, so nothing is lost. */
        mmkRenderNote();
      });
  }

  /* ------------------------------------------------------------------
   * Tell the visitor their cart was changed for them, and offer the way
   * back, because multi-paper pricing assumes one subject per pack and
   * only they know whether that is true.
   * ---------------------------------------------------------------- */
  function mmkRenderNote() {
    var host = document.querySelector('[data-shlc-mmk-notes]');
    if (!host) return;

    var raw = sessionStorage.getItem(MMK_NOTE);
    if (!raw) return;

    var note;
    try {
      note = JSON.parse(raw);
    } catch (e) {
      sessionStorage.removeItem(MMK_NOTE);
      return;
    }
    if (!note || !note.levels || !note.levels.length) return;

    var what = note.levels
      .map(function (l) {
        return l.papers + ' ' + l.level + ' paper' + (l.papers === 1 ? '' : 's');
      })
      .join(' and ');

    var box = document.createElement('div');
    box.className = 'shlc-mmk shlc-mmk--done';

    var head = document.createElement('p');
    head.className = 'shlc-mmk__head';
    head.innerHTML =
      'Multi-paper discount applied. Your ' +
      what +
      ' are now priced as ' +
      (note.levels.length > 1 ? 'single packs' : 'a single pack') +
      ', saving <strong>£' +
      money(note.saving) +
      '</strong>.';

    var body = document.createElement('p');
    body.className = 'shlc-mmk__body';
    body.textContent =
      'First paper full price, every paper after it 30% off. Use the stepper on ' +
      'the line to change how many papers you want.';

    var undo = document.createElement('button');
    undo.type = 'button';
    undo.className = 'shlc-mmk__undo';
    undo.textContent = 'These are different subjects, split them back';

    box.appendChild(head);
    box.appendChild(body);
    box.appendChild(undo);
    host.appendChild(box);

    undo.addEventListener('click', function () {
      if (!note.restore || !note.restore.length) return;
      busy(undo);

      var addedIds = (note.added || []).map(function (i) {
        return i.id;
      });

      fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var updates = {};
          cart.items.forEach(function (i) {
            if (addedIds.indexOf(i.variant_id) !== -1) updates[i.key] = 0;
          });
          return mmkRepack(updates, note.restore);
        })
        .then(function () {
          sessionStorage.removeItem(MMK_NOTE);
          resetGiftGuards();
          window.location.reload();
        })
        .catch(function () {
          unbusy(undo);
          showError('Those packs could not be split back. Please try again.');
        });
    });
  }

  /* ------------------------------------------------------------------
   * The papers stepper and the no-JavaScript fallback button. Both
   * carry a ready-made payload, so this only has to post it.
   * ---------------------------------------------------------------- */
  function wireMmkSwap() {
    document.querySelectorAll('[data-shlc-mmk-swap]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();

        var items = mmkParse(btn.getAttribute('data-shlc-mmk-swap'));
        var keys = (btn.getAttribute('data-shlc-mmk-keys') || '').split('|').filter(Boolean);
        if (!items.length || !keys.length) return;

        busy(btn);

        var updates = {};
        keys.forEach(function (k) {
          updates[k] = 0;
        });

        mmkRepack(updates, items)
          .then(function () {
            /* An explicit press is the visitor asking for the packed
               shape, so any earlier "leave my packs split" decision and
               the note that went with it are both spent. */
            sessionStorage.removeItem(MMK_GUARD);
            sessionStorage.removeItem(MMK_NOTE);
            resetGiftGuards();
            window.location.reload();
          })
          .catch(function () {
            unbusy(btn);
            showError('Those papers could not be combined. Please try again.');
          });
      });
    });
  }

  /* ------------------------------------------------------------------
   * "No thanks" hides the swap-up for the rest of the session.
   * ---------------------------------------------------------------- */
  function wireSwapDismiss() {
    var panel = document.querySelector('[data-shlc-swap-panel]');
    if (!panel) return;

    var key = 'shlcSwapDismissed:' + panel.getAttribute('data-shlc-swap-panel');
    if (sessionStorage.getItem(key)) {
      panel.hidden = true;
      return;
    }

    var no = panel.querySelector('[data-shlc-swap-no]');
    if (!no) return;
    no.addEventListener('click', function () {
      sessionStorage.setItem(key, '1');
      panel.hidden = true;
    });
  }

  /* ------------------------------------------------------------------
   * Remove links. Dawn's own cart.js owns quantity, but we removed the
   * stepper, so removal posts directly and reloads.
   * ---------------------------------------------------------------- */
  function wireRemove() {
    document.querySelectorAll('[data-shlc-remove]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var lineKey = btn.getAttribute('data-shlc-remove');
        if (!lineKey) return;
        busy(btn);
        post(window.Shopify.routes.root + 'cart/change.js', { id: lineKey, quantity: 0 })
          .then(function () {
            resetGiftGuards();
            window.location.reload();
          })
          .catch(function () {
            unbusy(btn);
            showError('That item could not be removed. Please try again.');
          });
      });
    });
  }

  /* ------------------------------------------------------------------
   * Quantity. Only shippable lines and products tagged multi-buy get a
   * stepper; a duplicated download gets a single "change to 1" button.
   * Both post the target quantity Liquid already worked out, so this
   * never has to read the current value off the page.
   * ---------------------------------------------------------------- */
  function wireQuantity() {
    document.querySelectorAll('[data-shlc-qty]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var lineKey = btn.getAttribute('data-shlc-qty');
        var target = Number(btn.getAttribute('data-shlc-qty-to'));
        if (!lineKey || !isFinite(target) || target < 1) return;
        busy(btn);
        post(window.Shopify.routes.root + 'cart/change.js', { id: lineKey, quantity: target })
          .then(function () {
            /* The subtotal moved, so the gift may now qualify or stop qualifying. */
            resetGiftGuards();
            window.location.reload();
          })
          .catch(function () {
            unbusy(btn);
            showError('That quantity could not be updated. Please try again.');
          });
      });
    });
  }

  /* ------------------------------------------------------------------
   * Mobile checkout bar. Only shown once the real Check out button has
   * scrolled out of view, so the two never compete on screen.
   * ---------------------------------------------------------------- */
  function wireStickyBar(root) {
    var bar = document.querySelector('[data-shlc-stickybar]');
    var target = document.getElementById('checkout');
    if (!bar || !target) return;

    root.classList.add('shlc-cart--barred');

    if (!('IntersectionObserver' in window)) {
      bar.hidden = false;
      return;
    }

    new IntersectionObserver(
      function (entries) {
        bar.hidden = entries[0].isIntersecting;
      },
      { rootMargin: '0px 0px -40px 0px' }
    ).observe(target);
  }

  function init() {
    var root = document.querySelector('[data-shlc-cart]');
    if (!root) return;
    wireStickyBar(root);
    wireRemove();
    wireQuantity();
    wireAddButtons();
    wireSwap();
    wireSwapDismiss();
    wireMmkSwap();
    if (!syncGiftCode(root)) syncGift(root);
    mmkApply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
